import { computed, onMounted, onBeforeUnmount, ref, shallowRef } from 'vue';

// The banner and Home card share the same single-use browser installation event.
const deferredPrompt = shallowRef(null);
const isInstalled = ref(false);
const isInstalling = ref(false);
const platform = ref('desktop');
let consumers = 0;
let displayMode;

const updateInstalled = () => {
  isInstalled.value =
    displayMode.matches || window.navigator.standalone === true;
};
const onInstallPrompt = event => {
  event.preventDefault();
  deferredPrompt.value = event;
};
const onInstalled = () => {
  isInstalled.value = true;
  deferredPrompt.value = null;
};

const install = async () => {
  if (isInstalled.value) return 'accepted';
  if (isInstalling.value) return 'pending';
  const prompt = deferredPrompt.value;
  if (!prompt) return 'unavailable';
  deferredPrompt.value = null;
  isInstalling.value = true;
  try {
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') onInstalled();
    return outcome;
  } finally {
    isInstalling.value = false;
  }
};

export function usePwaInstall() {
  onMounted(() => {
    consumers += 1;
    if (consumers > 1) return;
    const {
      userAgent,
      platform: devicePlatform,
      maxTouchPoints,
    } = window.navigator;
    if (
      /iPhone|iPad|iPod/i.test(userAgent) ||
      (devicePlatform === 'MacIntel' && maxTouchPoints > 1)
    ) {
      platform.value = 'ios';
    } else if (/Android/i.test(userAgent)) {
      platform.value = 'android';
    } else if (
      /Mac/i.test(userAgent) &&
      /Safari/i.test(userAgent) &&
      !/Chrome|Chromium|Edg/i.test(userAgent)
    ) {
      platform.value = 'mac';
    } else {
      platform.value = 'desktop';
    }
    displayMode = window.matchMedia('(display-mode: standalone)');
    updateInstalled();
    displayMode.addEventListener?.('change', updateInstalled);
    window.addEventListener('beforeinstallprompt', onInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);
  });
  onBeforeUnmount(() => {
    consumers -= 1;
    if (consumers) return;
    displayMode?.removeEventListener?.('change', updateInstalled);
    window.removeEventListener('beforeinstallprompt', onInstallPrompt);
    window.removeEventListener('appinstalled', onInstalled);
    deferredPrompt.value = null;
  });
  return {
    canInstall: computed(() => Boolean(deferredPrompt.value)),
    isInstalled,
    isInstalling,
    platform,
    install,
  };
}
